import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';

/**
 * Hook to handle unsaved changes warnings
 *
 * @example
 * // With custom message (for i18n support)
 * const { checkUnsavedChanges } = useUnsavedChanges({
 *   isDirty: formState.isDirty,
 *   message: t('form.unsavedChanges') // Pass translated message
 * });
 *
 * @example
 * // With custom confirm dialog
 * const { checkUnsavedChanges } = useUnsavedChanges({
 *   isDirty: formState.isDirty,
 *   confirmDialog: async (message) => {
 *     return await confirm({ description: message });
 *   }
 * });
 */
interface UseUnsavedChangesOptions {
  isDirty: boolean;
  isSubmitting?: boolean;
  message?: string;
  confirmDialog?: (message: string) => Promise<boolean> | boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export function useUnsavedChanges({
  isDirty,
  isSubmitting = false,
  message = 'You have unsaved changes. Are you sure you want to leave?',
  confirmDialog,
  onConfirm,
  onCancel,
}: UseUnsavedChangesOptions) {
  const router = useRouter();

  // Helper function to show confirmation
  const showConfirmation = useCallback(
    async (msg: string): Promise<boolean> => {
      if (confirmDialog) {
        const result = confirmDialog(msg);
        return result instanceof Promise ? await result : result;
      }
      return window.confirm(msg);
    },
    [confirmDialog]
  );

  // Handle browser unload (refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isSubmitting]);

  // Handle Next.js route changes
  useEffect(() => {
    // Intercept pushState to catch programmatic navigation
    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      const url = args[2] as string;
      if (isDirty && !isSubmitting && url !== window.location.pathname) {
        // Use async confirmation
        (async () => {
          const confirmed = await showConfirmation(message);
          if (!confirmed) {
            onCancel?.();
            return;
          }
          onConfirm?.();
          // If confirmed, proceed with navigation
          originalPushState.apply(window.history, args);
        })();
        return;
      }
      return originalPushState.apply(window.history, args);
    };

    // Handle popstate (back/forward button)
    const handlePopState = (_e: PopStateEvent) => {
      if (isDirty && !isSubmitting) {
        // Use async confirmation
        (async () => {
          const confirmed = await showConfirmation(message);
          if (!confirmed) {
            // Restore the current state
            window.history.pushState(null, '', window.location.pathname);
            onCancel?.();
            return;
          }
          onConfirm?.();
        })();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      // Restore original pushState
      window.history.pushState = originalPushState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty, isSubmitting, message, showConfirmation, onConfirm, onCancel, router]);

  // Provide a method to check before navigation
  const checkUnsavedChanges = useCallback(async (): Promise<boolean> => {
    if (isDirty && !isSubmitting) {
      const confirmed = await showConfirmation(message);
      if (!confirmed) {
        onCancel?.();
        return false;
      }
      onConfirm?.();
    }
    return true;
  }, [isDirty, isSubmitting, message, showConfirmation, onConfirm, onCancel]);

  return {
    checkUnsavedChanges,
  };
}
